import React, { Component } from 'react';
import { connect } from 'react-redux';
import ErrorBoundary from 'app/common/ErrorBoundary';
import { Outlet, useNavigate } from 'react-router-dom';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import StyleSheet from 'stilr';
import { findMatch, optimizedResize, findBreakpoints, desk } from 'react-flexr';

import { logout } from './redux/modules/auth';

import Nav from './layout/nav';
import Drawer from './layout/drawer';
import { DRAWER_WIDTH } from './layout/nav-styles';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import blue from '@mui/material/colors/blue';
import red from '@mui/material/colors/red';

import DateFnsLocale from './lib/date-fns-locale-da';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import I18nContext from 'app/common/i18n-context';

const muiTheme = createTheme({
  palette: {
    primary: blue,
    secondary: red
  }
});

const styles = StyleSheet.create({
  containerWithDrawer: {
    marginLeft: DRAWER_WIDTH,
    transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 50ms'
  },
  container: {
    marginLeft: 0,
    transition: 'all 350ms cubic-bezier(0.23, 1, 0.32, 1) 0ms'
  },
  content: {
    margin: 'calc(11px + 64px) 11px 22px',
    [desk]: {
      margin: 'calc(22px + 64px) 36px 48px'
    }
  }
});

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      desktop: Boolean(findMatch('desk')),
      showDrawer: false
    };
  }

  componentDidMount() {
    optimizedResize.init(this.onResize);
  }

  componentWillUnmount() {
    optimizedResize.remove(this.onResize);
  }

  onResize = () => {
    const breakpoints = findBreakpoints();
    if (breakpoints) {
      this.setState({ desktop: breakpoints.indexOf('desk') !== -1 });
    }
  };

  toggleDrawer = (showDrawer) => {
    this.setState({ showDrawer });
  };

  logout = () => {
    this.props.logout();

    if (this.props.navigate) {
      this.props.navigate('/', { replace: true });
    }
  };

  render() {
    const { desktop } = this.state;

    const showDrawer = desktop || this.state.showDrawer;
    const navProps = { desktop, user: this.props.user, logout: this.logout };

    return (
      <ThemeProvider theme={muiTheme}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={DateFnsLocale}
        >
          <I18nContext.Provider value={this.props.i18n}>
            <ErrorBoundary>
              <div
                className={
                  desktop ? styles.containerWithDrawer : styles.container
                }
              >
                <Nav
                  drawer={showDrawer}
                  toggleDrawer={this.toggleDrawer}
                  {...navProps}
                />

                <div className={`content-body ${styles.content}`}>
                  <Outlet />
                </div>
              </div>

              <Drawer
                toggle={this.toggleDrawer}
                show={showDrawer}
                {...navProps}
              />

              <ToastContainer
                position="bottom-center"
                autoClose={10000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
                limit={3}
              />
            </ErrorBoundary>
          </I18nContext.Provider>
        </LocalizationProvider>
      </ThemeProvider>
    );
  }
}

function AppWithNavigate(props) {
  const navigate = useNavigate();
  return <App {...props} navigate={navigate} />;
}

export default connect(({ i18n, auth: { user } }) => ({ i18n, user }), {
  logout
})(AppWithNavigate);